import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Zap, KeyRound, PlugZap, Building2, CalendarRange, AlertTriangle, CheckCircle2,
  RefreshCcw, ListChecks, ShieldAlert, Percent, Copy, Lock, ShieldCheck,
  BarChart3, ArrowRight, Stethoscope, ChevronDown, ChevronUp,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { getUser } from "../../lib/auth";

const PILOT_HOUSE_ID = "0ce3fb5d-c7bf-49cc-bc24-ca037d51dcfa";
const PILOT_HOUSE_NAME = "Casa Vento Sul — Gravatá";

const STATE_META = {
  not_configured:   { label: "Não configurada", tone: "muted", icon: KeyRound },
  key_present:      { label: "Chave cadastrada", tone: "info",  icon: KeyRound },
  connection_valid: { label: "Conexão validada", tone: "ok",    icon: PlugZap },
  mapping_pending:  { label: "Aguardando mapeamento", tone: "warn", icon: Building2 },
  mapped:           { label: "Imóvel mapeado", tone: "info",   icon: Building2 },
  synced:           { label: "Sincronização concluída", tone: "ok", icon: CheckCircle2 },
  error:            { label: "Erro de conexão", tone: "error", icon: AlertTriangle },
};

const TONE = {
  muted: "bg-slate-100 text-slate-700 border-slate-200",
  info:  "bg-sky-50 text-sky-800 border-sky-200",
  ok:    "bg-emerald-50 text-emerald-800 border-emerald-200",
  warn:  "bg-amber-50 text-amber-900 border-amber-200",
  error: "bg-rose-50 text-rose-800 border-rose-200",
};

function StatePill({ state }) {
  const meta = STATE_META[state] || STATE_META.not_configured;
  const Icon = meta.icon;
  return (
    <span
      data-testid={`pricelabs-state-${state}`}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${TONE[meta.tone]}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {meta.label}
    </span>
  );
}

function Section({ icon: Icon, title, description, children, right }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-6 mb-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <Icon className="w-5 h-5 text-[#0AA89C] mt-0.5" />
          <div>
            <h2 className="font-semibold text-[#22302E]">{title}</h2>
            {description && <p className="text-xs text-slate-500 mt-0.5 max-w-3xl">{description}</p>}
          </div>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function fmtBRL(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function maskId(id) {
  if (!id) return "—";
  const s = String(id);
  if (s.length <= 6) return `••••${s.slice(-2)}`;
  return `${s.slice(0, 3)}••••${s.slice(-3)}`;
}

async function copyToClipboard(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label || "Copiado"}: ${text}`);
  } catch {
    toast.error("Falha ao copiar.");
  }
}

export default function AdminPriceLabs() {
  const user = getUser();
  const isDiretor = user?.role === "diretor";

  const [status, setStatus] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [listings, setListings] = useState([]);
  const [listingsCachedAt, setListingsCachedAt] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [logs, setLogs] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({
    test: false, sync7: false, sync90: false, saveMap: false,
    rollback: false, homologate: false, diagnose: false,
  });
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagExpanded, setDiagExpanded] = useState(true);

  const [selectedListingId, setSelectedListingId] = useState("");
  const [rules, setRules] = useState({
    adjustment_percent: "0",
    min_price: "350",
    max_price: "3000",
  });
  const [formErrors, setFormErrors] = useState({});

  async function loadStatus()   { const r = await api.get("/admin/pricelabs/status"); setStatus(r.data); }
  async function loadListings() {
    try {
      const r = await api.get("/admin/pricelabs/listings");
      setListings(r.data.listings || []);
      setListingsCachedAt(r.data.cached_at || null);
    } catch { /* ignore */ }
  }
  async function loadMapping() {
    const r = await api.get(`/admin/pricelabs/houses/${PILOT_HOUSE_ID}/mapping`);
    setMapping(r.data);
    setSelectedListingId(r.data.listing_id || "");
    // Only override defaults if the user already saved values.
    setRules((prev) => ({
      adjustment_percent: r.data.adjustment_percent ?? prev.adjustment_percent,
      min_price: r.data.min_price ?? prev.min_price,
      max_price: r.data.max_price ?? prev.max_price,
    }));
  }
  async function loadLogs() {
    const r = await api.get("/admin/pricelabs/sync-logs", { params: { limit: 15 } });
    setLogs(r.data);
  }
  async function loadCalendar() {
    try {
      const r = await api.get(`/admin/pricelabs/houses/${PILOT_HOUSE_ID}/suggestions`, { params: { days: 90 } });
      setCalendar(r.data);
    } catch { setCalendar(null); }
  }
  async function loadReport() {
    try {
      const r = await api.get(`/admin/pricelabs/houses/${PILOT_HOUSE_ID}/sync-report/latest`);
      setReport(r.data?.ok ? r.data : null);
    } catch { setReport(null); }
  }

  async function loadAll() {
    setLoading(true);
    try {
      await Promise.all([loadStatus(), loadListings(), loadMapping(), loadLogs(), loadCalendar(), loadReport()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar status.");
    } finally { setLoading(false); }
  }

  useEffect(() => { loadAll(); }, []);

  async function onTestConnection() {
    setBusy((b) => ({ ...b, test: true }));
    try {
      const r = await api.post("/admin/pricelabs/test-connection");
      toast.success(r.data.message || "Conexão validada.");
      await Promise.all([loadStatus(), loadLogs(), loadListings()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao validar conexão.");
      await Promise.all([loadStatus(), loadLogs()]);
    } finally {
      setBusy((b) => ({ ...b, test: false }));
    }
  }

  function validateRules(values) {
    const err = {};
    const adj = Number(values.adjustment_percent);
    const mn = values.min_price === "" ? null : Number(values.min_price);
    const mx = values.max_price === "" ? null : Number(values.max_price);
    if (Number.isNaN(adj)) err.adjustment_percent = "Ajuste inválido.";
    else if (adj <= -100) err.adjustment_percent = "Ajuste não pode reduzir a 0% ou menos.";
    if (mn !== null && (Number.isNaN(mn) || mn <= 0)) err.min_price = "Preço mínimo deve ser maior que zero.";
    if (mx !== null && (Number.isNaN(mx) || mx <= 0)) err.max_price = "Preço máximo deve ser maior que zero.";
    if (mn !== null && mx !== null && mn >= mx) err.max_price = "Preço máximo deve ser maior que o mínimo.";
    return err;
  }

  async function onSaveMapping() {
    const errs = validateRules(rules);
    if (!selectedListingId) errs.listing_id = "Selecione o imóvel na lista acima.";
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(Object.values(errs)[0]);
      return;
    }
    setBusy((b) => ({ ...b, saveMap: true }));
    try {
      const picked = listings.find((l) => l.id === selectedListingId);
      const payload = {
        listing_id: selectedListingId,
        pms: picked?.pms || null,
        listing_name: picked?.name || null,
        currency: picked?.currency || null,
        timezone: null, // PriceLabs listings endpoint does not currently expose TZ per listing
        adjustment_percent: String(rules.adjustment_percent || "0"),
        min_price: rules.min_price === "" ? null : String(rules.min_price),
        max_price: rules.max_price === "" ? null : String(rules.max_price),
      };
      await api.put(`/admin/pricelabs/houses/${PILOT_HOUSE_ID}/mapping`, payload);
      toast.success("Mapeamento salvo.");
      await Promise.all([loadMapping(), loadStatus()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar mapeamento.");
    } finally {
      setBusy((b) => ({ ...b, saveMap: false }));
    }
  }

  // C6: mapeia status HTTP + código de erro para mensagens específicas ao usuário.
  function _translateSyncError(err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || "";
    // Correlation ID pode vir embutido no detail: "... correlation_id=xxxx"
    const cidMatch = /correlation_id=([\w-]+)/.exec(detail);
    const cid = cidMatch ? cidMatch[1] : null;
    let msg;
    if (status === 401 || status === 403) {
      msg = "Credencial da PriceLabs inválida ou sem permissão.";
    } else if (status === 404) {
      msg = "O imóvel mapeado não foi encontrado na PriceLabs.";
    } else if (status === 429) {
      msg = "O limite temporário de requisições da PriceLabs foi atingido. Tente novamente em alguns minutos.";
    } else if (status === 504) {
      msg = "A PriceLabs não respondeu dentro do tempo esperado.";
    } else if (status === 502 && detail.includes("PRICELABS_SCHEMA_UNRECOGNIZED")) {
      msg = "A PriceLabs retornou um formato de dados não reconhecido. Informe o código de diagnóstico ao suporte.";
    } else if (status === 502) {
      msg = "O serviço PriceLabs está temporariamente indisponível.";
    } else if (status === 409) {
      msg = detail || "Sincronização já em andamento ou pré-requisito ausente.";
    } else {
      msg = detail || "Falha na sincronização.";
    }
    return cid ? `${msg} (ref: ${cid})` : msg;
  }

  async function onSync(horizonDays, key) {
    // C5: impede clique duplo E sincronização paralela 7↔90.
    if (busy.sync7 || busy.sync90) return;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const r = await api.post(
        `/admin/pricelabs/houses/${PILOT_HOUSE_ID}/sync`,
        { horizon_days: horizonDays },
      );
      const rejTotal = r.data.records_rejected || 0;
      const cid = r.data.correlation_id ? ` · ref: ${r.data.correlation_id}` : "";
      toast.success(
        `Sync ${horizonDays}d: ${r.data.records_accepted} aceito(s), ${rejTotal} rejeitado(s).${cid}`,
      );
      await Promise.all([loadStatus(), loadLogs(), loadCalendar(), loadReport(), loadMapping()]);
    } catch (e) {
      toast.error(_translateSyncError(e));
      await Promise.all([loadStatus(), loadLogs()]);
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  }

  async function onHomologate() {
    if (!isDiretor) return;
    if (!window.confirm("Marcar mapeamento como homologado? Isso libera a sincronização de 90 dias.")) return;
    setBusy((b) => ({ ...b, homologate: true }));
    try {
      await api.post(`/admin/pricelabs/houses/${PILOT_HOUSE_ID}/homologate`);
      toast.success("Homologação registrada.");
      await Promise.all([loadMapping(), loadStatus()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na homologação.");
    } finally {
      setBusy((b) => ({ ...b, homologate: false }));
    }
  }

  async function onDeHomologate() {
    if (!isDiretor) return;
    if (!window.confirm("Revogar homologação? A sincronização de 90 dias voltará a ficar bloqueada.")) return;
    try {
      await api.post(`/admin/pricelabs/houses/${PILOT_HOUSE_ID}/de-homologate`);
      toast.success("Homologação revogada.");
      await Promise.all([loadMapping(), loadStatus()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao revogar.");
    }
  }

  async function onDiagnose() {
    setBusy((b) => ({ ...b, diagnose: true }));
    try {
      const r = await api.post(
        `/admin/pricelabs/houses/${PILOT_HOUSE_ID}/diagnose`,
        { horizon_days: 7 },
      );
      setDiagnostic(r.data);
      setDiagExpanded(true);
      if (r.data.ok) {
        toast.success(`Diagnóstico OK: ${r.data.records_received} registro(s) recebido(s).`);
      } else {
        toast.error(`Diagnóstico: ${r.data.error_code || "erro"} — veja detalhes abaixo.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao executar diagnóstico.");
    } finally {
      setBusy((b) => ({ ...b, diagnose: false }));
    }
  }

  async function onRollback() {
    if (!isDiretor) return;
    if (!window.confirm("Reverter a última sincronização? As sugestões da última importação serão removidas.")) return;
    setBusy((b) => ({ ...b, rollback: true }));
    try {
      const r = await api.post(`/admin/pricelabs/houses/${PILOT_HOUSE_ID}/rollback`);
      toast.success(`Removidas ${r.data.removed_suggestions} sugestão(ões).`);
      await Promise.all([loadStatus(), loadLogs(), loadCalendar(), loadReport()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no rollback.");
    } finally {
      setBusy((b) => ({ ...b, rollback: false }));
    }
  }

  const canTest = status?.can_test_connection;
  const hasListings = listings.length > 0;
  const pilotMapped = !!(mapping?.listing_id && mapping?.pms);
  const homologated = !!mapping?.homologated_at;
  const canSync7 = status?.can_test_connection && pilotMapped;
  const canSync90 = canSync7 && homologated;

  const calendarItems = calendar?.items || [];
  const withPrice = calendarItems.filter((i) => i.suggested_price);

  if (loading) {
    return <div className="text-slate-500 text-sm" data-testid="pricelabs-loading">Carregando…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-pricelabs">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-slate-500">
            Configurações → Integrações
          </div>
          <h1 className="font-display text-3xl text-[#22302E] mt-1 flex items-center gap-2">
            <Zap className="w-6 h-6 text-[#0AA89C]" />
            PriceLabs
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Fase 1 — <strong>somente sugestão</strong>. As sugestões da PriceLabs ficam disponíveis
            para consulta e ajuste local, mas <strong>não são publicadas automaticamente</strong> no
            site público, <strong>não sobrescrevem reservas</strong> e <strong>nada é enviado de
            volta</strong> à PriceLabs.
          </p>
        </div>
        <StatePill state={status?.state || "not_configured"} />
      </div>

      {/* Key + connection */}
      <Section
        icon={KeyRound}
        title="Chave da API"
        description="A chave PRICELABS_API_KEY é lida do ambiente do servidor. Ela nunca aparece no navegador, em logs ou em respostas de API."
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-slate-200 rounded-md p-4 bg-slate-50/60">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Status</div>
            <div className="text-sm font-medium mt-1" data-testid="pricelabs-key-status">
              {status?.key_configured ? "Chave cadastrada" : "Chave não configurada"}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Preview: <code className="px-1.5 py-0.5 rounded bg-white border">
                {status?.key_masked || "—"}
              </code>
            </div>
          </div>
          <div className="border border-slate-200 rounded-md p-4 bg-white">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Como configurar</div>
            <ol className="text-xs text-slate-700 mt-2 space-y-1 list-decimal pl-4">
              <li>Painel Emergent → seu app → <strong>Secrets / Environment</strong></li>
              <li>Adicione (ou edite) <code>PRICELABS_API_KEY</code> como <em>backend secret</em></li>
              <li>Cole a chave gerada em <a href="https://app.pricelabs.co/apisettings" target="_blank" rel="noreferrer" className="text-[#0AA89C] underline">app.pricelabs.co/apisettings</a></li>
              <li>Salve e reinicie o backend</li>
              <li>Volte aqui e clique em <strong>Testar conexão</strong></li>
            </ol>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            data-testid="pricelabs-test-connection"
            onClick={onTestConnection}
            disabled={!canTest || busy.test}
            className="bg-[#0AA89C] hover:bg-[#088c82] text-white"
          >
            <PlugZap className="w-4 h-4 mr-2" />
            {busy.test ? "Testando…" : "Testar conexão"}
          </Button>
          <Button variant="outline" data-testid="pricelabs-refresh" onClick={loadAll}>
            <RefreshCcw className="w-4 h-4 mr-2" /> Recarregar
          </Button>
          {!canTest && (
            <span className="text-xs text-slate-500 inline-flex items-center gap-1">
              <ShieldAlert className="w-3.5 h-3.5" />
              Cadastre a chave secreta para habilitar este botão.
            </span>
          )}
        </div>
        {status?.message && (
          <p className="mt-3 text-xs text-slate-600 italic" data-testid="pricelabs-status-msg">
            {status.message}
          </p>
        )}
      </Section>

      {/* Listings summary card (only if we have any cached) */}
      {hasListings && (
        <Section
          icon={CheckCircle2}
          title={`Conexão validada · ${listings.length} imóvel(is) encontrado(s)`}
          description={`Último teste: ${listingsCachedAt ? new Date(listingsCachedAt).toLocaleString("pt-BR") : "—"}. Nome, PMS e moeda vieram diretamente da PriceLabs.`}
        >
          <div className="overflow-x-auto" data-testid="pricelabs-listings-summary">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
                  <th className="px-2 py-2">Nome</th>
                  <th className="px-2 py-2">PMS</th>
                  <th className="px-2 py-2">listing_id (mascarado)</th>
                  <th className="px-2 py-2">Moeda</th>
                  <th className="px-2 py-2">Cidade</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {listings.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100">
                    <td className="px-2 py-1.5 font-medium">{l.name || "—"}</td>
                    <td className="px-2 py-1.5">{l.pms || "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">{maskId(l.id)}</td>
                    <td className="px-2 py-1.5">{l.currency || "—"}</td>
                    <td className="px-2 py-1.5">{l.city || "—"}</td>
                    <td className="px-2 py-1.5">
                      <button
                        className="text-xs text-[#0AA89C] hover:underline inline-flex items-center gap-1"
                        onClick={() => copyToClipboard(l.id, "listing_id")}
                        data-testid={`pricelabs-copy-${l.id}`}
                      >
                        <Copy className="w-3 h-3" /> copiar id
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Mapping selector */}
      <Section
        icon={Building2}
        title={`Vincular imóvel piloto — ${PILOT_HOUSE_NAME}`}
        description={`Selecione qual imóvel da sua conta PriceLabs corresponde à Casa Vento Sul. listing_id, PMS e moeda são preenchidos automaticamente a partir da API — sem digitação manual.`}
      >
        {!hasListings ? (
          <div className="text-sm text-slate-500">
            Clique em <strong>Testar conexão</strong> acima para carregar seus imóveis PriceLabs.
          </div>
        ) : (
          <>
            <div className="space-y-2 mb-6" role="radiogroup" data-testid="pricelabs-listing-picker">
              {listings.map((l) => {
                const active = selectedListingId === l.id;
                return (
                  <label
                    key={l.id}
                    className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition ${active ? "border-[#0AA89C] bg-[#0AA89C]/5" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`pricelabs-listing-option-${l.id}`}
                  >
                    <input
                      type="radio"
                      name="pricelabs-listing"
                      className="mt-1"
                      checked={active}
                      onChange={() => setSelectedListingId(l.id)}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-[#22302E]">{l.name || "(sem nome)"}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        PMS: <strong className="text-slate-700">{l.pms || "—"}</strong>
                        {" · "}Moeda: <strong className="text-slate-700">{l.currency || "—"}</strong>
                        {l.city && <> · {l.city}</>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); copyToClipboard(l.id, "listing_id"); }}
                      className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" /> {maskId(l.id)}
                    </button>
                  </label>
                );
              })}
              {formErrors.listing_id && (
                <p className="text-xs text-rose-600">{formErrors.listing_id}</p>
              )}
            </div>

            {selectedListingId && (
              <div className="mb-6 grid md:grid-cols-4 gap-3 text-xs">
                {["listing_id", "pms", "currency", "listing_name"].map((k) => {
                  const picked = listings.find((l) => l.id === selectedListingId);
                  const val = picked ? (
                    k === "listing_id" ? maskId(picked.id) :
                    k === "listing_name" ? (picked.name || "—") :
                    (picked[k] || "—")
                  ) : "—";
                  return (
                    <div key={k} className="bg-slate-50 border border-slate-200 rounded p-2">
                      <div className="uppercase tracking-wider text-[10px] text-slate-500 flex items-center gap-1">
                        <Lock className="w-3 h-3" /> {k}
                      </div>
                      <div className="text-slate-800 font-medium mt-0.5">{val}</div>
                    </div>
                  );
                })}
              </div>
            )}

            <h3 className="text-sm font-semibold text-[#22302E] mb-2">Regras locais</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label className="flex items-center gap-1">
                  <Percent className="w-3.5 h-3.5" /> Ajuste local (%)
                </Label>
                <Input
                  data-testid="pricelabs-mapping-adjustment"
                  type="number" step="0.1"
                  value={rules.adjustment_percent}
                  onChange={(e) => setRules({ ...rules, adjustment_percent: e.target.value })}
                  placeholder="0"
                />
                {formErrors.adjustment_percent && (
                  <p className="text-xs text-rose-600 mt-1">{formErrors.adjustment_percent}</p>
                )}
              </div>
              <div>
                <Label>Preço mínimo (R$)</Label>
                <Input
                  data-testid="pricelabs-mapping-min"
                  type="number" step="0.01"
                  value={rules.min_price}
                  onChange={(e) => setRules({ ...rules, min_price: e.target.value })}
                  placeholder="350"
                />
                {formErrors.min_price && (
                  <p className="text-xs text-rose-600 mt-1">{formErrors.min_price}</p>
                )}
              </div>
              <div>
                <Label>Preço máximo (R$)</Label>
                <Input
                  data-testid="pricelabs-mapping-max"
                  type="number" step="0.01"
                  value={rules.max_price}
                  onChange={(e) => setRules({ ...rules, max_price: e.target.value })}
                  placeholder="3000"
                />
                {formErrors.max_price && (
                  <p className="text-xs text-rose-600 mt-1">{formErrors.max_price}</p>
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button
                data-testid="pricelabs-mapping-save"
                onClick={onSaveMapping}
                disabled={busy.saveMap}
                className="bg-[#22302E] text-white hover:bg-[#1a2523]"
              >
                {busy.saveMap ? "Salvando…" : "Salvar mapeamento"}
              </Button>
              {pilotMapped && (
                <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Mapeado: {mapping.listing_name || "—"} ({mapping.pms})
                </span>
              )}
            </div>
          </>
        )}
      </Section>

      {/* Sync + homologation */}
      <Section
        icon={RefreshCcw}
        title="Sincronização (somente sugestão)"
        description="Comece por 7 dias e homologue os dados antes de liberar 90 dias. Nada é publicado no site público."
      >
        <div className="flex flex-wrap items-center gap-3">
          <Button
            data-testid="pricelabs-sync-7"
            onClick={() => onSync(7, "sync7")}
            disabled={!canSync7 || busy.sync7 || busy.sync90}
            className="bg-[#0AA89C] text-white hover:bg-[#088c82]"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${busy.sync7 ? "animate-spin" : ""}`} />
            {busy.sync7 ? "Sincronizando…" : "Sincronizar 7 dias"}
          </Button>

          <Button
            data-testid="pricelabs-sync-90"
            onClick={() => onSync(90, "sync90")}
            disabled={!canSync90 || busy.sync90 || busy.sync7}
            className={canSync90 ? "bg-[#22302E] text-white hover:bg-[#1a2523]" : ""}
            variant={canSync90 ? "default" : "outline"}
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${busy.sync90 ? "animate-spin" : ""}`} />
            {busy.sync90 ? "Sincronizando…" : "Sincronizar 90 dias"}
          </Button>

          {!canSync7 && (
            <span className="text-xs text-slate-500">
              Habilite: conexão validada + mapeamento salvo.
            </span>
          )}
          {canSync7 && !homologated && (
            <span className="text-xs text-amber-700 inline-flex items-center gap-1">
              <ShieldAlert className="w-3.5 h-3.5" />
              90d bloqueado até homologação.
            </span>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-3">
          <Button
            data-testid="pricelabs-diagnose"
            onClick={onDiagnose}
            disabled={!canSync7 || busy.diagnose}
            variant="outline"
            className="border-sky-300 text-sky-800 hover:bg-sky-50"
          >
            <Stethoscope className={`w-4 h-4 mr-2 ${busy.diagnose ? "animate-pulse" : ""}`} />
            {busy.diagnose ? "Diagnosticando…" : "Diagnóstico técnico (sem gravar)"}
          </Button>
          {isDiretor && !homologated && (
            <Button
              data-testid="pricelabs-homologate"
              onClick={onHomologate}
              disabled={busy.homologate}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              {busy.homologate ? "Homologando…" : "Marcar como homologado"}
            </Button>
          )}
          {isDiretor && homologated && (
            <>
              <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" />
                Homologado por {mapping.homologated_by} em {new Date(mapping.homologated_at).toLocaleString("pt-BR")}
              </span>
              <Button
                variant="outline"
                onClick={onDeHomologate}
                data-testid="pricelabs-de-homologate"
                className="text-slate-600"
              >
                Revogar homologação
              </Button>
            </>
          )}
          {isDiretor && (
            <Button
              variant="outline"
              onClick={onRollback}
              disabled={busy.rollback}
              data-testid="pricelabs-rollback"
              className="text-rose-700 border-rose-200 ml-auto"
            >
              Reverter última sincronização
            </Button>
          )}
        </div>
      </Section>

      {/* Diagnostic panel */}
      {diagnostic && (
        <Section
          icon={Stethoscope}
          title="Diagnóstico técnico da chamada PriceLabs"
          description="Contrato verificado (método/endpoint/body), status HTTP real e forma da resposta. Nenhuma sugestão foi gravada."
          right={
            <button
              onClick={() => setDiagExpanded((v) => !v)}
              className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
              data-testid="pricelabs-diagnose-toggle"
            >
              {diagExpanded ? <><ChevronUp className="w-3 h-3" /> Recolher</> : <><ChevronDown className="w-3 h-3" /> Expandir</>}
            </button>
          }
        >
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <ReportCard label="Método" value={diagnostic.method || "—"} />
            <ReportCard label="Endpoint" value={diagnostic.endpoint || "—"} />
            <ReportCard label="Status HTTP" value={diagnostic.http_status ?? "—"} />
            <ReportCard label="Content-Type" value={diagnostic.content_type || "—"} />
            <ReportCard label="Root type" value={diagnostic.root_type || "—"} />
            <ReportCard label="Response shape" value={diagnostic.response_shape || "—"} />
            <ReportCard label="Body size" value={`${diagnostic.body_size ?? 0} bytes`} />
            <ReportCard label="Duração" value={`${diagnostic.duration_ms ?? "—"} ms`} />
            <ReportCard label="Registros recebidos" value={diagnostic.records_received ?? "—"} />
            <ReportCard label="Registros válidos" value={diagnostic.records_valid ?? "—"} />
            <ReportCard label="listing_id (mascarado)" value={diagnostic.listing_id_masked || "—"} />
            <ReportCard label="pms_name" value={diagnostic.pms_name || "—"} />
            <ReportCard label="Moeda" value={diagnostic.currency || "—"} />
            <ReportCard label="Correlation ID" value={diagnostic.correlation_id || "—"} />
          </div>

          {diagnostic.listing_error_status && (
            <div className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
              <strong>error_status por listing:</strong> {diagnostic.listing_error_status}
              {diagnostic.listing_last_refreshed && (
                <div className="text-xs mt-1">Última sincronização na PriceLabs: {diagnostic.listing_last_refreshed}</div>
              )}
            </div>
          )}

          {!diagnostic.ok && (
            <div className="mt-4 p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-900 text-sm">
              <strong>Erro classificado:</strong> {diagnostic.error_code}
              {diagnostic.error_message && <div className="text-xs mt-1 font-mono">{diagnostic.error_message}</div>}
            </div>
          )}

          {diagExpanded && (
            <>
              {diagnostic.top_level_keys && diagnostic.top_level_keys.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">top_level_keys</div>
                  <div className="text-xs font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded p-2">
                    {diagnostic.top_level_keys.join(", ")}
                  </div>
                </div>
              )}

              {diagnostic.sample && diagnostic.sample.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Amostra (3 primeiros dias)</div>
                  <pre className="text-xs font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto">
{JSON.stringify(diagnostic.sample, null, 2)}
                  </pre>
                </div>
              )}

              {diagnostic.request_body_sanitized && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Request body enviado (sanitizado)</div>
                  <pre className="text-xs font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto">
{JSON.stringify(diagnostic.request_body_sanitized, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}
        </Section>
      )}

      {/* Sync report */}
      {report && (
        <Section
          icon={BarChart3}
          title="Relatório da última sincronização"
          description={`Sync ${report.horizon_days || "?"} dia(s) · ${report.status} · executado por ${report.triggered_by || "—"}.`}
        >
          <div className="grid md:grid-cols-4 gap-3 mb-4">
            <ReportCard label="Dias solicitados / retornados" value={`${report.days_requested ?? "—"} / ${report.days_returned ?? "—"}`} />
            <ReportCard label="Aceitos / Rejeitados" value={`${report.records_accepted ?? "—"} / ${report.records_rejected ?? "—"}`} />
            <ReportCard label="Duração da chamada" value={`${report.request_duration_ms ?? "—"} ms`} />
            <ReportCard label="Moeda · Timezone" value={`${report.currency || "—"} · ${report.timezone || "—"}`} />
            <ReportCard label="Preço mínimo (PriceLabs)" value={fmtBRL(report.price_stats?.min)} />
            <ReportCard label="Preço máximo (PriceLabs)" value={fmtBRL(report.price_stats?.max)} />
            <ReportCard label="Preço médio (PriceLabs)" value={fmtBRL(report.price_stats?.mean)} />
            <ReportCard label="Preço mediano (PriceLabs)" value={fmtBRL(report.price_stats?.median)} />
            <ReportCard label="min_stay observado" value={report.min_stay_observed?.min ? `${report.min_stay_observed.min} – ${report.min_stay_observed.max} noite(s)` : "—"} />
            <ReportCard label="Clamp piso ⤓ / teto ⤒" value={`${report.clamped_by_floor ?? 0} / ${report.clamped_by_ceiling ?? 0}`} />
            <ReportCard label="Inseridos / Atualizados / Preservados" value={`${report.records_inserted ?? 0} / ${report.records_updated ?? 0} / ${report.records_preserved ?? 0}`} />
            <ReportCard label="Início → Fim" value={`${report.started_at?.slice(11,19) || "—"} → ${report.finished_at?.slice(11,19) || "—"}`} />
          </div>

          {report.divergence && (
            <div className="p-4 rounded-md border border-sky-200 bg-sky-50/60">
              <div className="text-[11px] uppercase tracking-widest text-sky-800">Divergência vs. preço público</div>
              <div className="mt-2 flex items-center gap-3 text-sm">
                <span className="text-slate-700">Público: <strong>{fmtBRL(report.divergence.public_price)}</strong></span>
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <span className="text-slate-700">Média PriceLabs: <strong>{fmtBRL(report.divergence.mean_suggested)}</strong></span>
                <span className={Number(report.divergence.diff_abs) >= 0 ? "text-emerald-700" : "text-rose-700"}>
                  ({Number(report.divergence.diff_abs) >= 0 ? "+" : ""}
                  {fmtBRL(report.divergence.diff_abs)} · {report.divergence.diff_pct}%)
                </span>
              </div>
            </div>
          )}

          {Object.entries(report.rejection_reasons || {}).filter(([, v]) => v > 0).length > 0 && (
            <div className="mt-4 text-xs text-slate-600">
              <div className="uppercase tracking-widest text-[10px] text-slate-500 mb-1">Motivos de rejeição</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(report.rejection_reasons)
                  .filter(([, v]) => v > 0)
                  .map(([k, v]) => (
                    <span key={k} className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200">
                      {k}: {v}
                    </span>
                  ))}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Calendar */}
      <Section
        icon={CalendarRange}
        title="Calendário de sugestões (90 dias)"
        description="Comparativo entre a sugestão da PriceLabs e o preço final após o ajuste local. Nenhum destes preços é publicado automaticamente."
        right={<div className="text-xs text-slate-500">{withPrice.length} dia(s) com sugestão</div>}
      >
        {calendarItems.length === 0 ? (
          <p className="text-sm text-slate-500">
            Nenhuma sugestão importada ainda. Após salvar o mapeamento, rode Sincronizar 7 dias.
          </p>
        ) : (
          <div className="overflow-x-auto -mx-2" data-testid="pricelabs-calendar">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
                  <th className="px-2 py-2">Data</th>
                  <th className="px-2 py-2">Sugestão PriceLabs</th>
                  <th className="px-2 py-2">Ajuste %</th>
                  <th className="px-2 py-2">Preço final (local)</th>
                  <th className="px-2 py-2">Min. noites</th>
                  <th className="px-2 py-2">Reserva?</th>
                </tr>
              </thead>
              <tbody>
                {calendarItems.map((it) => (
                  <tr key={it.date} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-2 py-1.5 font-mono text-xs">{it.date}</td>
                    <td className="px-2 py-1.5">{fmtBRL(it.suggested_price)}</td>
                    <td className="px-2 py-1.5">
                      {it.adjustment_percent !== null && it.adjustment_percent !== undefined
                        ? `${it.adjustment_percent}%` : "—"}
                    </td>
                    <td className="px-2 py-1.5 font-medium">{fmtBRL(it.final_price)}</td>
                    <td className="px-2 py-1.5">{it.min_stay ?? "—"}</td>
                    <td className="px-2 py-1.5">
                      {it.has_reservation
                        ? <span className="text-rose-700 text-xs">Ocupado</span>
                        : <span className="text-emerald-700 text-xs">Livre</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Audit logs */}
      <Section
        icon={ListChecks}
        title="Auditoria de sincronização"
        description="Últimas 15 chamadas à PriceLabs realizadas por este painel. Nenhuma credencial aparece nestes registros."
      >
        {logs.length === 0 ? (
          <p className="text-sm text-slate-500">Sem registros ainda.</p>
        ) : (
          <div className="overflow-x-auto" data-testid="pricelabs-logs">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left uppercase tracking-wider text-slate-500 border-b">
                  <th className="px-2 py-2">Quando</th>
                  <th className="px-2 py-2">Tipo</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2" title="Solicitado / Retornado">Dias (req/ret)</th>
                  <th className="px-2 py-2" title="Aceitos / Rejeitados">Aceitos / Rej.</th>
                  <th className="px-2 py-2" title="Inseridos / Atualizados / Preservados">I / U / P</th>
                  <th className="px-2 py-2" title="Piso / Teto">Clamp ⤓/⤒</th>
                  <th className="px-2 py-2">Estado</th>
                  <th className="px-2 py-2">API (ms)</th>
                  <th className="px-2 py-2">Por</th>
                  <th className="px-2 py-2">Mensagem</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => {
                  const req = l.days_requested ?? l.days_fetched ?? 0;
                  const ret = l.days_returned ?? l.days_fetched ?? 0;
                  const acc = l.records_accepted ?? l.suggestions_stored ?? 0;
                  const rej = l.records_rejected ?? 0;
                  const ins = l.records_inserted ?? 0;
                  const upd = l.records_updated ?? 0;
                  const prs = l.records_preserved ?? 0;
                  const cf = l.clamped_by_floor ?? 0;
                  const cc = l.clamped_by_ceiling ?? 0;
                  const reqMs = l.request_duration_ms ?? l.duration_ms ?? 0;
                  const stateBefore = l.integration_state_before || "—";
                  const stateAfter = l.integration_state_after || "—";
                  const actor = l.triggered_by || l.actor_id || "—";
                  const rejEntries = Object.entries(l.rejection_reasons || {})
                    .filter(([, v]) => Number(v) > 0)
                    .map(([k, v]) => `${k}: ${v}`).join(" · ");
                  return (
                    <tr key={l.id} className="border-b border-slate-100">
                      <td className="px-2 py-1.5 font-mono">{l.created_at?.slice(0, 19).replace("T", " ")}</td>
                      <td className="px-2 py-1.5">{l.kind}</td>
                      <td className="px-2 py-1.5">
                        <span className={
                          l.status === "ok" ? "text-emerald-700"
                          : l.status === "partial" ? "text-amber-700"
                          : "text-rose-700"}>
                          {l.status}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">{req} / {ret}</td>
                      <td className="px-2 py-1.5" title={rejEntries || "sem rejeições"}>{acc} / {rej}</td>
                      <td className="px-2 py-1.5">{ins} / {upd} / {prs}</td>
                      <td className="px-2 py-1.5">{cf} / {cc}</td>
                      <td className="px-2 py-1.5 text-slate-600">
                        <span className="text-[10px]">{stateBefore} → {stateAfter}</span>
                      </td>
                      <td className="px-2 py-1.5">{reqMs}ms</td>
                      <td className="px-2 py-1.5">{actor}</td>
                      <td className="px-2 py-1.5 text-slate-600 truncate max-w-[240px]" title={l.error_message || ""}>
                        {l.error_message || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

function ReportCard({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-[#22302E] mt-1">{value}</div>
    </div>
  );
}
